import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Divider, IconButton, Icon } from '@mui/material';
import { IDispatchInstructionLine } from '../../@types/model/dispatch/dispatchInstructionLine';
import { RootState, useAppSelector } from '../../@types/redux';
import PillButton from '../../components/input/PillButton';
import { generateBarcodesFromDispatch } from '../../services/compliance/documentService';
import { IDispatchInstruction } from '../../@types/model/dispatch/dispatchInstruction';
import { dataSetDispatchInfoData, mapDashboardDispatchLines } from '../../store/data/Functions';
import DispatchHttpService from '../../services/http/dispatch/dispatchHttpService';
import { IDispatchView } from '../../@types/model/dispatch/dispatchView';
import { generalShowErrorSnackbar } from '../../store/general/Functions';
import { ITemperatureUnit } from '../../@types/model/dispatch/temperatureUnit';
import { PackmanLink } from '../../components/link/packmanLink';
import CustomTooltip from '../../components/tooltip/tooltip';
import { syncMasterData } from '../../services/masterDataSyncService';
import { getIconLocation } from '../../services/iconHelperService';
import { IDashboardDispatchInstructionLine } from '../../@types/model/dispatch/dashboardDispatchInstructionLine';

interface IDispatchInstructionInfoPopupProps {
    editDispatchLine : (line : IDispatchInstructionLine) => void;
    status : string;
    selectedDispatchView : IDispatchView;
    setLoading : (isLoading ?: boolean) => void;
}

const DispatchInstructionInfoPopup = (props : IDispatchInstructionInfoPopupProps) : React.ReactElement => {
    const [selectedDispatch, setSelectedDispatch] = useState<IDispatchInstruction | undefined>();

    // Master data and state via hooks (functional component pattern)
    const sites = useAppSelector(state => state.masterData.sites);
    const carriers = useAppSelector(state => state.masterData.carriers);
    const trips = useAppSelector(state => state.data.trips);
    const auth = useAppSelector(state => state.auth);
    type DashboardLinesSelector = (state : RootState) => Array<IDashboardDispatchInstructionLine>;
    const dashboardDispatchInstructionLines = useAppSelector(mapDashboardDispatchLines as unknown as DashboardLinesSelector);
    const stocks = useAppSelector(state => state.data.stocks);

    // Additional master data used for code lookups
    const organizations = useAppSelector(state => state.masterData.organizations);
    const commodities = useAppSelector(state => state.masterData.commodities);
    const varieties = useAppSelector(state => state.masterData.varieties);
    const markets = useAppSelector(state => state.masterData.markets);
    const packs = useAppSelector(state => state.masterData.packs);
    const sizes = useAppSelector(state => state.masterData.sizes);
    const colours = useAppSelector(state => state.masterData.colours);
    const grades = useAppSelector(state => state.masterData.grades);

    useEffect(() => {
        let isMounted = true;
        const run = async () => {
            try {
                props.setLoading(true);
                const isIndexedDBAvailable = !!self.indexedDB ? true : false;

                if (isIndexedDBAvailable) {
                    await syncMasterData(false);
                }

                const res = await DispatchHttpService.getDispatchInfoData(props.selectedDispatchView.id, !isIndexedDBAvailable);
                if (!isMounted) { return; }

                if (res) {
                    dataSetDispatchInfoData(res.data);
                    setSelectedDispatch(res.data.dispatch);
                }
            } catch {
                if (isMounted) {
                    generalShowErrorSnackbar('Failed to load dispatch info data');
                }
            } finally {
                if (isMounted) {
                    props.setLoading(false);
                }
            }
        };
        run();
        return () => { isMounted = false; };
    }, [props.selectedDispatchView.id]);

    const generateBarcodeToPDF = () => {
        if (selectedDispatch) {
            const siteCode = sites.find(x => x.id === selectedDispatch?.sourceSiteId)?.code;
            generateBarcodesFromDispatch(selectedDispatch, siteCode);
        }
    };

    const hasGenerateBarcodeRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(x => x.isActive && x.code === 'DISPATCH_GENERATE_BARCODES');
    }, [auth]);

    const getCarrierName = (carrierId ?: number) => {
        const carrier = carriers.find(x => x.id === carrierId);
        return carrier?.name;
    };

    const getDevices = (tempUnits : Array<ITemperatureUnit>) => tempUnits.filter(x => x.isActive).map(x => x.deviceNumber).toString().replace(/,/g, ', ');

    // Lookup helpers from master data
    const getOrganizationCode = (id : number) => organizations.find(x => x.id === id)?.code;
    const getCommodityCode = (id : number) => commodities.find(x => x.id === id)?.code;
    const getVarietyCode = (id : number) => varieties.find(x => x.id === id)?.code;
    const getMarketCode = (id : number) => markets.find(x => x.id === id)?.code;
    const getPackCode = (id : number) => packs.find(x => x.id === id)?.code;
    const getSizeCode = (id : number) => sizes.find(x => x.id === id)?.code;
    const getColourCode = (id ?: number) => colours.find(x => x.id === id)?.code;
    const getGradeCode = (id : number) => grades.find(x => x.id === id)?.code;

    const getStockBarcode = (stockId : number) => stocks.find(x => x.id === stockId)?.barcode || '';
    const getStockCartons = (stockId : number) => stocks.find(x => x.id === stockId)?.cartons || 0;

    const trip = trips.find(x => x.id === props.selectedDispatchView.tripId);
    const dashboardLines = dashboardDispatchInstructionLines.filter(x => x.dispatchId === selectedDispatch?.id);

    if (!dashboardLines || dashboardLines.length === 0) {
        return <div className={'h200'}/>;
    }

    return (
        <div style={{ maxHeight: 700 }} className={'fdc hfill'}>
            {!!trip ?
                <div className={`${props.status === 'Incoming' ? 'fdc' : 'dn'}`}>
                    <div className={'pl10 pr10 fs12 fw500'}>{`Trip: ${trip?.id}`}</div>
                    <div className={'fdr'} >
                        <div className={'pl10 pr10 fs12'}>Description:</div>
                        <div className={'pr10 fs12 fw500'}>{trip?.description}</div>
                    </div>
                    <div className={'fdr'} >
                        <div className={'pl10 pr10 fs12'}>Driver:</div>
                        <div className={'pr10 fs12 fw500'}>{trip?.driver}</div>
                    </div>
                    <div className={'fdr'} >
                        <div className={'pl10 pr10 fs12'}>Container:</div>
                        <div className={'pr10 fs12 fw500'}>{trip?.container}</div>
                    </div>
                    <div className={'fdr'} >
                        <div className={'pl10 pr10 fs12'}>Container Tare Weight:</div>
                        <div className={'pr10 fs12 fw500'}>{trip?.containerTareWeight}</div>
                    </div>
                    <div className={'fdr'} >
                        <div className={'pl10 pr10 fs12'}>Reg Nr:</div>
                        <div className={'pr10 fs12 fw500'}>{trip?.registrationNumber}</div>
                    </div>
                    <div className={'fdr'} >
                        <div className={'pl10 pr10 fs12'}>Carrier:</div>
                        <div className={'pr10 fs12 fw500'}>{getCarrierName(trip?.carrierId ?? 0)}</div>
                    </div>
                    <div className={'fdr mb5'} >
                        <div className={'pl10 pr10 fs12'}>Devices:</div>
                        <div className={'pr10 fs12 fw500'}>{getDevices(trip?.temperatureUnits ?? [])}</div>
                    </div>
                </div>
                :
                <div className={`${props.status === 'Incoming' ? 'aic jcc p10' : 'dn'}`}>
                    No Trips Assigned
                </div>
            }
            <PillButton
                color={'secondary'}
                className={'m10 h30'}
                text={'Generate Barcodes'}
                disabled={!hasGenerateBarcodeRight}
                onClick={generateBarcodeToPDF}/>
            <Divider className={'wfill mt5 mb5'} />
            <div style={{ maxHeight: 700 }} className={'hfill oya oxh p10'}>
                {dashboardLines.map((instructionLine, lineIndex) => {
                    return <div key={`instruction-${instructionLine.organizationId}-${lineIndex}`}>
                        <div className={'fdr aic wfill'}>
                            <div className={'fdc'}>
                                <div className={'fdr'}>
                                    {getOrganizationCode(instructionLine.organizationId)  + ' | '  + getCommodityCode(instructionLine.commodityId)}
                                </div>
                                <div className={'fdr'}>
                                    {getVarietyCode(instructionLine.varietyId) + ' | ' + getMarketCode(instructionLine.targetMarketId)}
                                </div>
                                <div className={'fdr'}>
                                    {getPackCode(instructionLine.packId) + ' | ' + getSizeCode(instructionLine.sizeId)}
                                </div>
                                <div className={'fdr'}>
                                    {getColourCode(instructionLine.colourId) + ' | '  + getGradeCode(instructionLine.gradeId)}
                                </div>
                            </div>
                            <div className={'flx1'} />
                            <div className={'fdc'}>
                                <div className={'h30 w30 Round bcpd cs fdc fs12 aic jcc'}>
                                    {instructionLine.dispatchLines.length}
                                </div>
                            </div>
                        </div>
                        <Divider className={'wfill mt5 mb5'} />
                        {instructionLine.dispatchLines.map(x => <div className={'fdr'} key={`line-${x.currentStockId}`}>
                            <div className={'fdr flx1'}>
                                <PackmanLink
                                    type={'transactions'}
                                    targetPage={'stock'}
                                    id={Number(x.currentStockId)}
                                    text={getStockBarcode(x.currentStockId)} />
                                <div className='w5'/>
                                {`(${getStockCartons(x.currentStockId)} ea)`}
                            </div>
                            <div className={`${x.identified ? 'pt5' : 'dn'}`}>
                                <img height={25} src={`${getIconLocation()}/scanned.svg`} />
                            </div>
                            <CustomTooltip title={'Substitute this line'}>
                                <IconButton className={`p0 h30 w30 ${(props.status !== 'Dispatched' && props.status !== 'Received' && props.status !== 'Incoming') ? '' : 'dn'}`}
                                    onClick={() => props.editDispatchLine(x)}>
                                    <Icon fontSize={'small'}>edit</Icon>
                                </IconButton>
                            </CustomTooltip>
                        </div>)}
                        { (lineIndex < (dashboardLines.length - 1)) && <Divider className={'wfill mt5 mb5'} />}
                    </div>;
                })}
            </div>
        </div>
    );
};

export default DispatchInstructionInfoPopup;
